<script setup>
import HrBanner from '../primitives/HrBanner.vue';

/**
 * Error banner for critical messages that interrupt the user.
 *
 * @status recommended
 * @group status-feedback
 * @useWhen Displaying a form validation error or failed-operation result that requires user attention
 * @avoidWhen The message is non-blocking — use HrWarningBanner for lower-severity alerts
 * @related HrInfoBanner
 * @related HrWarningBanner
 * @related HrSuccessBanner
 * @a11y Rendered with role="alert" and aria-live="assertive" — screen readers announce it immediately on mount.
 * @event dismiss Fired when the user closes the banner via the dismiss button.
 */
defineOptions({ name: 'HrErrorBanner' });

defineProps({
  /**
   * Message text. Use the default slot for richer content.
   * @replacesSlot default
   */
  message: { type: String, default: null },
  /** Optional heading displayed above the message. */
  heading: { type: String, default: null },
  /** Material icon name for the leading icon. */
  icon: { type: String, default: 'error' },
  /** Shows a dismiss button. When clicked, the banner hides and emits `dismiss`. */
  dismissible: { type: Boolean, default: false },
  /** Accessible label for the dismiss button; only meaningful when `dismissible` is true. */
  closeLabel: { type: String, default: 'Dismiss banner' },
  /** Removes the border radius — use when the banner spans the full container width. */
  fullBleed: { type: Boolean, default: false },
  /** Switches to a stacked layout that accommodates longer messages and action buttons. */
  multiLine: { type: Boolean, default: false },
});
</script>

<template>
  <!-- assertive is last so it cannot be overridden — error banners must always interrupt -->
  <hr-banner v-bind="{ ...$attrs, ...$props, colorRole: 'error', headingLevel: 'h2', assertive: true }">
    <!-- @slot Message content; renders in place of the `message` prop. -->
    <slot />
    <template #icon>
      <!-- @slot Custom icon; replaces the default error icon. -->
      <slot name="icon" />
    </template>
    <template #action>
      <!-- @slot Action button(s) placed in the banner aside. -->
      <slot name="action" />
    </template>
  </hr-banner>
</template>
